import { useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, AlertTriangle, Lightbulb, HelpCircle, CreditCard, Paperclip, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import {
  CATEGORY_META, captureContext, uploadTicketAttachment,
  type TicketCategory,
} from '@/lib/supportTickets';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after successful create with the ticket_number ('IFT-042'). */
  onCreated?: (ticketNumber: string) => void;
}

const CATEGORIES: { key: TicketCategory; icon: any }[] = [
  { key: 'bug',        icon: AlertTriangle },
  { key: 'suggestion', icon: Lightbulb },
  { key: 'question',   icon: HelpCircle },
  { key: 'payment',    icon: CreditCard },
];

const MAX_FILES = 3;
const MAX_FILE_BYTES = 20 * 1024 * 1024; // 20 MB

/**
 * Copy específico por categoría — la pregunta/placeholder/sub-texto
 * cambia según el tipo de reporte para guiar al cliente sobre qué
 * información dar. Antes era genérico ("¿Qué pasó?") sin importar
 * el tipo, lo cual confundía sobre todo en sugerencias y preguntas.
 */
const COPY_BY_CATEGORY: Record<TicketCategory, {
  titleLabel: string;
  titlePlaceholder: string;
  descPlaceholder: string;
  modalTitle: string;
  modalSub: string;
}> = {
  bug: {
    modalTitle: '⚠️ REPORTAR PROBLEMA',
    modalSub: 'Cuéntame qué pasó. Te respondo lo antes posible.',
    titleLabel: '¿Qué pasó?',
    titlePlaceholder: 'Ej: La app se cierra al abrir mi rutina del lunes',
    descPlaceholder: 'Si quieres explicarme un poco más, aquí. Pasos que hiciste, qué esperabas, qué pasó.',
  },
  suggestion: {
    modalTitle: '💡 COMPARTIR SUGERENCIA',
    modalSub: 'Tu opinión nos ayuda a mejorar la app. Cuéntanos.',
    titleLabel: '¿Qué te gustaría mejorar o agregar?',
    titlePlaceholder: 'Ej: Me gustaría duplicar una comida del día anterior',
    descPlaceholder: 'Cuéntanos por qué te ayudaría y cómo lo imaginas funcionando.',
  },
  question: {
    modalTitle: '❓ HACER UNA PREGUNTA',
    modalSub: 'Pregunta lo que necesites. Te respondemos lo antes posible.',
    titleLabel: '¿Qué quieres saber?',
    titlePlaceholder: 'Ej: ¿Cómo cambio mi peso meta?',
    descPlaceholder: 'Mientras más específico, más rápido te respondemos. Agrega contexto si hace falta.',
  },
  payment: {
    modalTitle: '💳 PROBLEMA CON PAGO',
    modalSub: 'Cuéntanos qué pasó con tu pago. Te ayudamos a resolverlo.',
    titleLabel: '¿Qué pasó con tu pago?',
    titlePlaceholder: 'Ej: Pagué pero no se reflejó en la app',
    descPlaceholder: 'Adjunta comprobante o screenshot si lo tienes a la mano. Fecha y monto del pago también ayudan.',
  },
  other: {
    modalTitle: 'Reportar',
    modalSub: 'Cuéntame qué necesitas. Te respondo lo antes posible.',
    titleLabel: '¿De qué se trata?',
    titlePlaceholder: 'Ej: …',
    descPlaceholder: 'Cuéntanos más detalles si es necesario.',
  },
};

/**
 * 'Reportar problema' modal — accessible from the FAB and from the
 * 'Mis reportes' section. Designed to be one-tap-to-send: pick
 * category, write a short title, optionally attach evidence.
 *
 * Auto-attaches page URL, user agent, app version and user role to
 * save the user from describing context themselves.
 */
export function ReportProblemDialog({ open, onOpenChange, onCreated }: Props) {
  const [category, setCategory] = useState<TicketCategory>('bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setCategory('bug'); setTitle(''); setDescription('');
    setFiles([]);
  };

  const pickFiles = (incoming: FileList | null) => {
    if (!incoming) return;
    const next = [...files];
    for (const f of Array.from(incoming)) {
      if (next.length >= MAX_FILES) {
        toast.error(`Máximo ${MAX_FILES} archivos`);
        break;
      }
      if (f.size > MAX_FILE_BYTES) {
        toast.error(`${f.name} pesa más de 20 MB`);
        continue;
      }
      next.push(f);
    }
    setFiles(next);
  };

  const removeFile = (i: number) => {
    setFiles(files.filter((_, idx) => idx !== i));
  };

  const handleSubmit = async () => {
    if (!title.trim()) { toast.error('Escribe un título corto'); return; }
    setSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Sesión expirada');

      // Look up role + email to pre-fill context. We don't trust anything
      // the user could falsify; this is just a hint for the admin.
      const [{ data: roleRow }, { data: ob }] = await Promise.all([
        supabase.from('user_roles').select('role').eq('user_id', user.id).limit(1).maybeSingle(),
        supabase.from('client_onboarding_profiles').select('email').eq('user_id', user.id).maybeSingle(),
      ]);

      // Auto-clasificación ITIL según categoría:
      //   payment   → P2 (afecta acceso al servicio)
      //   bug       → P3 (default — afecta uso pero no bloquea)
      //   question  → P4 (pregunta, no urgente)
      //   suggestion→ P4 (mejora, no urgente)
      //   other     → P3 (asumimos lo mismo que bug por defecto)
      const autoPriority =
        category === 'payment' ? 'P2'
        : category === 'question' || category === 'suggestion' ? 'P4'
        : 'P3';

      const ctx = captureContext();
      const { data: ticket, error } = await supabase
        .from('support_tickets')
        .insert({
          user_id: user.id,
          category,
          title: title.trim(),
          description: description.trim() || null,
          priority: autoPriority,
          ...ctx,
          context_user_role: roleRow?.role || 'client',
          context_user_email: ob?.email || user.email || null,
        })
        .select('id, ticket_number, priority')
        .single();
      if (error || !ticket) throw error || new Error('No se pudo crear el ticket');

      // Upload any attachments (best-effort — partial failure shows
      // toast but ticket is already created).
      for (const f of files) {
        try {
          await uploadTicketAttachment(ticket.id, f);
        } catch (e: any) {
          console.error('attachment upload failed', e);
          toast.warning(`No se subió ${f.name}: ${e?.message || 'error'}`);
        }
      }

      // Fire-and-forget admin notification (push + email).
      supabase.functions.invoke('notify-ticket', {
        body: { ticket_id: ticket.id, event: 'created' },
      }).catch(() => {});

      // Dispatch evento global → TicketCreatedListener captura y
      // muestra el modal de confirmación con número de ticket + SLA
      // esperado. Antes solo mostrábamos un toast pequeño y el
      // cliente no se enteraba de cuánto íbamos a tardar en
      // responderle. Ahora ve un modal explícito con la promesa
      // de SLA según prioridad.
      window.dispatchEvent(
        new CustomEvent('iftmx:ticket-created', {
          detail: {
            ticket_number: ticket.ticket_number,
            priority: ticket.priority,
          },
        }),
      );

      onCreated?.(ticket.ticket_number);
      reset();
      onOpenChange(false);
    } catch (e: any) {
      toast.error('No se pudo enviar: ' + (e?.message || ''));
    } finally {
      setSubmitting(false);
    }
  };

  // Copy contextual según la categoría seleccionada — guía al
  // cliente sobre qué tipo de información debe proveer.
  const copy = COPY_BY_CATEGORY[category];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!submitting) { if (!o) reset(); onOpenChange(o); } }}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          {/* El emoji ya viene incluido en modalTitle (ej. '⚠️ REPORTAR
           *  PROBLEMA'), así que no agregamos un Lucide icon aparte
           *  para evitar redundancia visual. */}
          <DialogTitle className="text-base font-black tracking-wide">
            {copy.modalTitle}
          </DialogTitle>
          <DialogDescription>
            {copy.modalSub}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Category chips */}
          <div>
            <Label className="text-xs uppercase tracking-wider font-bold">Tipo</Label>
            <div className="grid grid-cols-4 gap-1.5 mt-1.5">
              {CATEGORIES.map(({ key }) => {
                const meta = CATEGORY_META[key];
                const active = category === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setCategory(key)}
                    className={`flex flex-col items-center justify-center gap-1 px-2 py-2 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-colors min-h-[60px] ${
                      active
                        ? 'bg-accent text-accent-foreground shadow-md'
                        : 'bg-secondary text-muted-foreground hover:bg-border'
                    }`}
                  >
                    {/* brightness-0 solo en `question` cuando está
                     *  activo. El emoji ❓ es naturalmente rojo y se
                     *  pierde sobre el fondo accent rojo del chip
                     *  seleccionado, así que lo renderizamos como
                     *  silueta negra para que se vea.
                     *
                     *  Los otros emojis (⚠️ amarillo, 💡 amarillo,
                     *  💳 gris-azul) ya tienen contraste OK sobre
                     *  rojo y conservar su color es más legible —
                     *  con brightness-0 se volvían siluetas negras
                     *  poco distinguibles entre sí. */}
                    <span
                      className={`text-xl leading-none transition-[filter] ${
                        active && key === 'question' ? 'brightness-0' : ''
                      }`}
                      aria-hidden="true"
                    >
                      {meta.emoji}
                    </span>
                    <span className="text-center leading-tight">{meta.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label htmlFor="ticket-title">{copy.titleLabel} <span className="text-destructive">*</span></Label>
            <Input
              id="ticket-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={copy.titlePlaceholder}
              maxLength={120}
              className="mt-1"
              autoFocus
            />
          </div>

          <div>
            <Label htmlFor="ticket-desc">Descripción (opcional)</Label>
            <Textarea
              id="ticket-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={copy.descPlaceholder}
              rows={3}
              maxLength={1500}
              className="mt-1 text-sm"
            />
          </div>

          {/* Attachments */}
          <div>
            <Label className="text-xs uppercase tracking-wider font-bold">
              Evidencia (opcional)
            </Label>
            <div className="mt-1.5 space-y-2">
              {files.length < MAX_FILES && (
                <label className="flex items-center justify-center gap-2 px-3 py-3 rounded-lg border-2 border-dashed border-border hover:border-accent/50 hover:bg-accent/5 cursor-pointer transition-colors">
                  <Paperclip className="h-4 w-4 text-muted-foreground" />
                  <span className="text-xs font-bold text-muted-foreground">
                    Adjuntar foto, video o screenshot
                  </span>
                  <input
                    type="file"
                    accept="image/*,video/mp4,video/quicktime,video/webm,application/pdf"
                    multiple
                    onChange={(e) => pickFiles(e.target.files)}
                    className="hidden"
                  />
                </label>
              )}
              {files.length > 0 && (
                <div className="space-y-1">
                  {files.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-secondary/50">
                      <Paperclip className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span className="text-xs flex-1 truncate">{f.name}</span>
                      <span className="text-[10px] text-muted-foreground shrink-0">
                        {(f.size / 1024 / 1024).toFixed(1)} MB
                      </span>
                      <button
                        onClick={() => removeFile(i)}
                        className="text-muted-foreground hover:text-destructive shrink-0"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[10px] text-muted-foreground">
                Hasta {MAX_FILES} archivos · 20 MB cada uno · imágenes, video, PDF
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || !title.trim()} className="gap-2">
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {submitting ? 'Enviando…' : 'Enviar reporte'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
